// ModerationPage — the Apple guideline 1.2 report queue.
//
// An app with user-generated content is rejected without a queue a human actually
// works, so this page is a shipping requirement for the Community tab, not a nicety.
// Everything on it comes from sql/078:
//   · moderation_queue()                — this operator's own Local reports
//   · platform_moderation_queue(p_all)  — platform admin; default = the global queue
//   · moderate(report, 'hide'|'dismiss')
//   · operator_post / platform_post     — the composer
//
// THE OWNER'S DECISION, WHICH THIS PAGE MUST NOT BLUR: a report on a GLOBAL (Tips)
// post belongs to the platform alone and an operator never sees it; an operator's
// queue is only their own Local content. So the two are never one list — a platform
// admin gets an explicit switch, and even the "every operator" view keeps Tips in
// its own titled section rather than sprinkling it through the operators' rows.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

const HJ = () => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${getAuthToken()}`,
  "Content-Type": "application/json",
});
const rpc = async (fn, body = {}) => {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, { method: "POST", headers: HJ(), body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.text()) || fn);
  return r.json();
};

// The five values the reports.reason CHECK allows, spelled the way a person would.
const REASON = {
  spam: "Spam",
  abuse: "Abuse or harassment",
  offensive: "Offensive",
  off_topic: "Off topic",
  other: "Something else",
};
const REASON_BADGE = { spam: "badge-gold", abuse: "badge-red", offensive: "badge-red", off_topic: "badge-gray", other: "badge-gray" };

const ago = (ts) => {
  if (!ts) return "";
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// The RPCs raise named errors; turn the ones an operator can actually hit into
// sentences. Anything unrecognised keeps the caller's fallback rather than dumping
// a Postgres string on screen.
const friendly = (e, fallback) => {
  const m = String((e && e.message) || "");
  if (/platform_admin_required/.test(m)) return "That queue belongs to the platform owner.";
  if (/owner_or_admin_only/.test(m)) return "Moderation is for the owner or an admin on this account.";
  if (/not_your_queue/.test(m)) return "That report isn't yours to act on.";
  if (/not_found/.test(m)) return "That report is gone — someone may have handled it already.";
  return fallback;
};
const isGone = (e) => /not_found/.test(String((e && e.message) || ""));

export function ModerationPage({ showToast, isPlatformAdmin }) {
  const isMobile = useIsMobile();
  // Platform admins start on the global queue. "all" is only ever reached by an
  // explicit click, because it is the one view that spans other people's queues.
  const [view, setView] = useState("tips");
  const queue = isPlatformAdmin ? view : "mine";

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [busy, setBusy] = useState(false);
  // A failed hide/dismiss must be visible on the panel the operator is looking at.
  // A toast is the nicety; if showToast was never passed, or the operator has already
  // scrolled past where it lands, the toast is the whole error report and the row just
  // sits there looking untouched. This says so in place, next to the buttons.
  const [actErr, setActErr] = useState(null);

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const d = isPlatformAdmin
        ? await rpc("platform_moderation_queue", { p_all: queue === "all" })
        : await rpc("moderation_queue");
      // A slower earlier response must not land on top of the queue the operator is
      // looking at now — these two lists are different queues, not two pages of one.
      if (mine !== seq.current) return;
      setRows(Array.isArray(d) ? d : []);
      setErr(null);
    } catch (e) {
      if (mine !== seq.current) return;
      setErr(friendly(e, "Couldn't load the moderation queue."));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [isPlatformAdmin, queue]);
  useEffect(() => { load(); }, [load]);

  // Switching queues drops the old rows immediately. If the new load then fails we
  // show the failure over an empty list — the alternative is the previous queue's
  // reports sitting under the new heading, which is how someone hides a Tips post
  // thinking it was an operator's.
  //
  // Two things have to happen here that cannot wait for the effect:
  //
  //  · The in-flight request is invalidated NOW, on the click. `load` only bumps the
  //    sequence when the effect runs it, which is after this render commits — and the
  //    old queue's fetch can resolve inside that gap, still matching the sequence it
  //    was issued with. It then calls setRows and the previous queue's reports land
  //    under the new heading anyway. Worse, if the new load subsequently fails, those
  //    foreign rows stay on screen under the "last good copy" banner, which tells the
  //    operator that Tips reports ARE this operator's queue. Bumping the ref here
  //    makes every response issued before the click unusable.
  //
  //  · Loading goes up NOW, for the same gap. With rows emptied and loading still
  //    false from the previous load, the render between this click and the effect
  //    draws the empty state — "Every queue is clear. Nothing is waiting anywhere on
  //    the platform." — before a single byte has been read for that queue. An
  //    all-clear is the one message this page must never show on no evidence.
  function switchTo(v) {
    if (v === view) return;
    seq.current += 1;
    setView(v);
    setRows([]);
    setSelId(null);
    setActErr(null);
    setErr(null);
    setLoading(true);
  }

  const sel = rows.find((r) => r.id === selId) || null;

  function openReport(id) {
    setSelId(id);
    setActErr(null);
  }

  async function act(action) {
    if (!sel || busy) return;
    const id = sel.id;
    const isComment = !!sel.comment_id;
    setBusy(true);
    setActErr(null);
    try {
      await rpc("moderate", { p_report: id, p_action: action });
      setSelId(null);
      // The row is resolved, so neither queue RPC (both filter status='open') will
      // return it again. Dropping it here keeps the list honest even if the refresh
      // below fails, instead of leaving a row the operator was just told they closed.
      setRows((rs) => rs.filter((r) => r.id !== id));
      showToast && showToast(
        action === "hide"
          ? `Hidden from the app. The ${isComment ? "comment" : "post"} stays on file for review.`
          : "Report dismissed. Nothing in the app changed.",
        "success",
      );
      load();
    } catch (e) {
      const msg = friendly(e, "That didn't go through — nothing was changed.");
      // not_found is the one failure where the row genuinely is not there any more
      // (someone else worked it first). Leaving it in the list would invite the
      // operator to keep clicking a button that can only ever fail.
      if (isGone(e)) {
        setSelId(null);
        setRows((rs) => rs.filter((r) => r.id !== id));
        load();
      } else {
        setActErr(msg);
      }
      showToast && showToast(msg, "error");
    } finally {
      setBusy(false);
    }
  }

  // In the "every operator" view the rows arrive from several queues at once, so they
  // get grouped and titled. Tips is pinned to the top and named, because that is the
  // one group no operator can see or act on.
  const groups = useMemo(() => {
    if (queue !== "all") return [{ key: "single", title: null, sub: null, rows }];
    const by = new Map();
    for (const r of rows) {
      const k = r.organization_id || "__tips__";
      if (!by.has(k)) by.set(k, { key: k, global: !r.organization_id, title: r.organization || null, rows: [] });
      by.get(k).rows.push(r);
    }
    return [...by.values()]
      .sort((a, b) => (a.global === b.global ? String(a.title || "").localeCompare(String(b.title || "")) : a.global ? -1 : 1))
      .map((g) => ({
        ...g,
        title: g.global ? "Tips" : g.title,
        sub: g.global
          ? "The platform's own queue. No operator sees these."
          : "This operator's own feed — they can work these too.",
      }));
  }, [rows, queue]);

  const heading = {
    mine: "Reports your customers filed about your own posts and comments.",
    tips: "Reports about Tips — the platform's own posts. Operators never see these.",
    all: "Every queue on the platform, kept apart. Acting on an operator's report acts for them.",
  }[queue];

  // "Waiting on you" is true of your own queue and of Tips, which only the platform
  // owner works. It is NOT true of the "every operator" view: most of those reports
  // belong to an operator who can and should work them, and telling the owner that
  // forty other people's reports are waiting on them is a number that means nothing.
  const countLine = queue === "all"
    ? `${rows.length} open across every queue`
    : `${rows.length} waiting on you`;

  const emptyLine = {
    mine: "Nothing to review. Your customers haven't reported anything — that's the good outcome.",
    tips: "Nothing reported about Tips. Nobody has flagged a platform post.",
    all: "Every queue is clear. Nothing is waiting anywhere on the platform.",
  }[queue];

  const detail = sel ? (
    <Detail r={sel} queue={queue} busy={busy} actErr={actErr} onAct={act} onClose={() => { setSelId(null); setActErr(null); }} />
  ) : null;

  return (
    <div className="calm-pane">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Moderation</div>
          <div style={{ fontSize: 12.5, color: "var(--text2)" }}>{heading}</div>
        </div>
        {isPlatformAdmin ? (
          <div className="seg">
            <button className={view === "tips" ? "on" : ""} onClick={() => switchTo("tips")}>Reports about Tips</button>
            <button className={view === "all" ? "on" : ""} onClick={() => switchTo("all")}>Every operator</button>
          </div>
        ) : null}
      </div>

      {/* A failed refresh over rows that are still on screen is a different sentence
          from a failed first load, and both are different from an empty queue. */}
      {err && rows.length > 0 ? (
        <div className="alert alert-error">
          {err} What's below is the last good copy, so it may be out of date.
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div style={{ padding: 20, color: "var(--text2)" }}>Loading the queue…</div>
      ) : err && rows.length === 0 ? (
        <div className="card" style={{ padding: 28 }}>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>{err}</p>
          <p style={{ color: "var(--text2)", fontSize: 13.5 }}>
            This is not an empty queue — nothing was read from the server. Reload the page;
            if it keeps failing, sign out and back in.
          </p>
          <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={load}>Try again</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--text2)" }}>{emptyLine}</div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 440px", minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: "var(--text2)", marginBottom: 8 }}>
              {countLine}
            </div>
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              {groups.map((g) => (
                <div key={g.key}>
                  {queue === "all" ? (
                    <div style={{ padding: "10px 14px", background: "var(--bg)", borderTop: "1px solid var(--border)" }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                        {g.title || `${g.rows.length} report${g.rows.length === 1 ? "" : "s"}`}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--text3)" }}>{g.sub}</div>
                    </div>
                  ) : null}
                  {g.rows.map((r) => (
                    <QueueRow key={r.id} r={r} selected={selId === r.id} onOpen={() => openReport(r.id)} />
                  ))}
                </div>
              ))}
            </div>
          </div>
          {!isMobile && sel ? <div style={{ flex: "0 0 380px", position: "sticky", top: 76 }}>{detail}</div> : null}
        </div>
      )}

      <Composer isPlatformAdmin={isPlatformAdmin} showToast={showToast} />

      {isMobile && sel ? (
        <>
          <div className="modal-overlay" onClick={() => { setSelId(null); setActErr(null); }} />
          <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 60, maxHeight: "85dvh", overflowY: "auto" }}>
            {detail}
          </div>
        </>
      ) : null}
    </div>
  );
}

function QueueRow({ r, selected, onOpen }) {
  const isComment = !!r.comment_id;
  return (
    <div
      className="alist-row"
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); }
      }}
      style={{
        cursor: "pointer",
        background: selected ? "var(--accent-weak)" : undefined,
        borderLeft: selected ? "2px solid var(--accent)" : "2px solid transparent",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span className={`badge ${REASON_BADGE[r.reason] || "badge-gray"}`}>{REASON[r.reason] || r.reason}</span>
          <span style={{ fontSize: 12.5, color: "var(--text2)" }}>
            {[isComment ? "Comment" : "Post", r.author].filter(Boolean).join(" · ")}
          </span>
        </div>
        <div style={{
          fontSize: 13.5, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis",
          whiteSpace: "nowrap", color: r.content ? undefined : "var(--text3)",
        }}>
          {r.content || "The reported content is no longer on file."}
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text3)", flexShrink: 0 }}>{ago(r.created_at)}</div>
    </div>
  );
}

function Detail({ r, queue, busy, actErr, onAct, onClose }) {
  const isComment = !!r.comment_id;
  const kind = isComment ? "comment" : "post";
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12.5, color: "var(--text2)", fontWeight: 600 }}>
            Reported {isComment ? "comment" : "post"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <span className={`badge ${REASON_BADGE[r.reason] || "badge-gray"}`}>{REASON[r.reason] || r.reason}</span>
            <span style={{ fontSize: 12, color: "var(--text3)" }}>{ago(r.created_at)}</span>
          </div>
        </div>
        <button className="close-btn" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {/* Only the platform's "every operator" view mixes queues, so only it needs the
          row to say which one this is. Elsewhere the page heading already said it. */}
      {queue === "all" ? (
        <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--text2)" }}>
          {r.organization
            ? `${r.organization}'s queue — their own feed.`
            : "The platform's queue. This one is Tips, and no operator can see it."}
        </div>
      ) : null}

      {r.author ? (
        <div style={{ marginTop: 12, fontSize: 13.5, color: "var(--text2)" }}>Written by {r.author}</div>
      ) : null}

      {r.content ? (
        <blockquote style={{ margin: "8px 0 0", padding: "10px 12px", background: "var(--bg)", borderRadius: "var(--r-sm)", fontSize: 15, whiteSpace: "pre-wrap" }}>
          {r.content}
        </blockquote>
      ) : (
        <div style={{ marginTop: 8, fontSize: 13.5, color: "var(--text3)" }}>
          The reported {kind} is no longer on file. Dismissing is the only thing left to do here.
        </div>
      )}

      {r.note ? (
        <>
          <div style={{ fontSize: 12.5, color: "var(--text2)", marginTop: 14, fontWeight: 600 }}>WHAT THE REPORTER SAID</div>
          <div style={{ marginTop: 4, fontSize: 14, whiteSpace: "pre-wrap" }}>{r.note}</div>
        </>
      ) : null}

      {/* The failure sits next to the buttons that produced it. Without this the only
          record of a refused moderation is a toast at the top of the document. */}
      {actErr ? (
        <div className="alert alert-error" style={{ marginTop: 14, marginBottom: 0 }}>
          {actErr} This report is still open.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn btn-danger" disabled={busy || !r.content} onClick={() => onAct("hide")}>
          Hide this {kind}
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={() => onAct("dismiss")}>
          Leave it up
        </button>
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "var(--text3)" }}>
        Hiding takes it out of the app for everyone straight away. It is not deleted — the {kind}
        {" "}and this report stay on file, so the decision can be reviewed later. Leaving it up closes
        the report and changes nothing.
      </div>
    </div>
  );
}

// The composer. Which feed a post lands in is the thing to get right: a post to Tips
// reaches every customer of every operator at once and there is no unsend, so the
// target is stated in full above the button and confirmed again on the way out.
function Composer({ isPlatformAdmin, showToast }) {
  const [open, setOpen] = useState(false);
  // Always defaults to the operator's own feed, even for a platform admin — the
  // wider blast radius has to be chosen on purpose.
  const [target, setTarget] = useState("operator");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const global = isPlatformAdmin && target === "global";
  const text = body.trim();

  async function send() {
    if (!text || sending) return;
    if (global && !window.confirm(
      "Post this to Tips?\n\nEvery customer of every operator on iScoopCRM will see it in their app. There is no unsend — it can only be hidden afterwards."
    )) return;
    setSending(true);
    setSendErr(null);
    try {
      // p_image is left off on purpose: there is no upload surface here, and a made-up
      // path would render as a broken image in every customer's feed.
      await rpc(global ? "platform_post" : "operator_post", { p_title: title.trim() || null, p_body: text });
      setTitle("");
      setBody("");
      setOpen(false);
      showToast && showToast(
        global ? "Posted to Tips. It's live for every customer now." : "Posted. Your customers will see it in the app.",
        "success",
      );
    } catch (e) {
      // The draft is deliberately kept and the composer stays open. Clearing it here
      // once threw away the text on a failure that never reached the database, and
      // the operator had no way to tell whether it had posted or not.
      const msg = friendly(e, "Couldn't post that — nothing was published.");
      setSendErr(msg);
      showToast && showToast(msg, "error");
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginTop: 18 }}>
        {/* The draft survives a close so a mis-click doesn't cost the text, but the
            previous failure must not: reopening with a stale "nothing went out"
            banner over a draft reads as a fresh failure of a post never attempted. */}
        <button className="btn btn-ghost" onClick={() => { setSendErr(null); setOpen(true); }}>Write a post</button>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 18, marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 15, fontWeight: 600 }}>Write a post</div>
        <button className="close-btn" onClick={() => setOpen(false)} aria-label="Close" disabled={sending}>✕</button>
      </div>

      {/* Locked while a post is in flight. The request already carries the target it
          was sent with, so flipping this mid-send would only change the labels on
          screen — leaving the operator reading "Post to Tips" over a post that went
          to their own customers, or the reverse. */}
      {isPlatformAdmin ? (
        <div className="seg" style={{ marginTop: 12 }}>
          <button className={target === "operator" ? "on" : ""} disabled={sending} onClick={() => setTarget("operator")}>Post as this operator</button>
          <button className={target === "global" ? "on" : ""} disabled={sending} onClick={() => setTarget("global")}>Post to Tips (everyone)</button>
        </div>
      ) : null}

      <label style={{ display: "block", marginTop: 12 }}>
        <span style={{ fontSize: 12.5, color: "var(--text2)" }}>Title (optional)</span>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
               style={{ width: "100%", marginTop: 4 }} />
      </label>

      <label style={{ display: "block", marginTop: 10 }}>
        <span style={{ fontSize: 12.5, color: "var(--text2)" }}>What you want to say</span>
        {/* 4,000 is the posts.body CHECK; the counter only appears near it so it isn't
            noise on a two-line post. */}
        <textarea className="input" value={body} maxLength={4000} rows={5}
                  onChange={(e) => setBody(e.target.value)} style={{ width: "100%", marginTop: 4 }} />
      </label>
      {body.length > 3600 ? (
        <div style={{ fontSize: 11.5, color: "var(--text3)" }}>{4000 - body.length} characters left</div>
      ) : null}

      {/* Red is deliberate on the global option. It is not an error — it is the one
          choice on this page that cannot be taken back. */}
      <div className={`alert ${global ? "alert-error" : "alert-info"}`} style={{ marginTop: 14, marginBottom: 0 }}>
        {global
          ? "This goes to Tips. Every customer of every operator on iScoopCRM sees it."
          : "This goes to your own feed. Only your customers see it."}
      </div>

      {sendErr ? (
        <div className="alert alert-error" style={{ marginTop: 10, marginBottom: 0 }}>
          {sendErr} Your draft is still here — nothing went out.
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button className="btn" disabled={sending || !text} onClick={send}>
          {global ? "Post to Tips" : "Post to my customers"}
        </button>
        <button className="btn btn-ghost" disabled={sending} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}
